import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  fetchPolicies,
  createPolicy,
  updatePolicy,
  deletePolicy,
} from "@/store/altingross/policies";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

// ─── Sabitler ────────────────────────────────────────────────────────────────
const POLICY_TYPES = [
  { value: 1, label: "Gizlilik Politikası" },
  { value: 2, label: "Kullanım Koşulları" },
  { value: 3, label: "KVKK Aydınlatma Metni" },
  { value: 4, label: "Çerez Politikası" },
  { value: 5, label: "Mesafeli Satış Sözleşmesi" },
  { value: 6, label: "Diğer" },
];

const POLICY_TYPE_COLORS = {
  1: "bg-blue-100 text-blue-700",
  2: "bg-green-100 text-green-700",
  3: "bg-purple-100 text-purple-700",
  4: "bg-orange-100 text-orange-700",
  5: "bg-red-100 text-red-700",
  6: "bg-gray-100 text-gray-600",
};

const policyTypeLabel = (v) => POLICY_TYPES.find((t) => t.value === v)?.label ?? "—";

const PAGE_SIZE = 20;

const EMPTY = {
  title: "",
  content: "",
  policyType: "",
  isActive: true,
  version: 1,
  order: 0,
  metaDescription: "",
  language: "tr",
};

const schema = Yup.object({
  title: Yup.string().trim().min(2, "En az 2 karakter").required("Zorunlu"),
  content: Yup.string().trim().min(1, "İçerik zorunlu").required("Zorunlu"),
  policyType: Yup.number().required("Politika türü seçin"),
  language: Yup.string().required("Zorunlu"),
  version: Yup.number().min(0),
  order: Yup.number().min(0),
});

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function Policies() {
  const dispatch = useDispatch();
  const { list, loading, pagination, savingId, deletingId } = useSelector(
    (s) => s.policies
  );

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    (p = page) => dispatch(fetchPolicies({ page: p, size: PAGE_SIZE })),
    [dispatch, page]
  );

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // ─── Form ──────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        title: values.title.trim(),
        content: values.content,
        policyType: Number(values.policyType),
        isActive: values.isActive,
        version: Number(values.version),
        order: Number(values.order),
        metaDescription: values.metaDescription.trim(),
        language: values.language.trim(),
      };

      let res;
      if (editing) {
        res = await dispatch(updatePolicy({ id: editing.id, data: payload }));
        if (!res.error) {
          toast.success("Politika güncellendi.");
          handleClose();
          load(page);
        } else {
          toast.error("Güncelleme başarısız.");
        }
      } else {
        res = await dispatch(createPolicy(payload));
        if (!res.error) {
          toast.success("Politika oluşturuldu.");
          handleClose();
          load(1);
          setPage(1);
        } else {
          toast.error("Oluşturma başarısız.");
        }
      }
    },
  });

  const handleOpen = (policy = null) => {
    setEditing(policy);
    formik.resetForm({
      values: policy
        ? {
            title: policy.title ?? "",
            content: policy.content ?? "",
            policyType: policy.policyType ?? "",
            isActive: policy.isActive ?? true,
            version: policy.version ?? 1,
            order: policy.order ?? 0,
            metaDescription: policy.metaDescription ?? "",
            language: policy.language ?? "tr",
          }
        : EMPTY,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditing(null);
    formik.resetForm({ values: EMPTY });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await dispatch(deletePolicy(deleteTarget.id));
    if (!res.error) {
      toast.success("Politika silindi.");
      load(page);
    } else {
      toast.error("Silme başarısız.");
    }
    setDeleteTarget(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="policies-header"
        title="Politikalar"
        description="Gizlilik, KVKK ve kullanım politikalarını yönetin."
        action={
          <Button size="sm" onClick={() => handleOpen()}>
            <Plus className="w-4 h-4 mr-1" />
            Yeni Ekle
          </Button>
        }
      />

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Header */}
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center border-b border-gray-100 px-4 py-2.5 gap-4">
          <span className="text-xs font-semibold text-black uppercase tracking-wide">Başlık</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden md:block">Tür</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Dil</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Durum</span>
          <span></span>
        </div>

        {loading && list.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor…
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Henüz politika eklenmedi.
          </div>
        ) : (
          <ul>
            {list.map((p, idx) => (
              <li
                key={p.id}
                className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center px-4 py-2.5 gap-4 ${
                  idx !== list.length - 1 ? "border-b border-gray-100" : ""
                } hover:bg-gray-50 transition-colors`}
              >
                {/* Başlık */}
                <span className="flex items-center gap-2 text-sm text-gray-800 font-medium truncate">
                  <FileText className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                  {p.title}
                </span>

                {/* Tür */}
                <span className="hidden md:block">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${POLICY_TYPE_COLORS[p.policyType] ?? "bg-gray-100 text-gray-600"}`}>
                    {policyTypeLabel(p.policyType)}
                  </span>
                </span>

                {/* Dil */}
                <span className="text-xs text-gray-500 hidden sm:block uppercase w-6 text-center">
                  {p.language ?? "—"}
                </span>

                {/* Durum */}
                <span className="hidden sm:block">
                  {p.isActive ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">Aktif</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">Pasif</span>
                  )}
                </span>

                {/* Aksiyonlar */}
                <span className="flex items-center gap-1">
                  {deletingId === p.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-gray-700"
                        onClick={() => handleOpen(p)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-red-500"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            <span>
              Toplam {pagination.count} kayıt — Sayfa {pagination.page}/{pagination.totalPages || 1}
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog — tam ekran, ikiye bölünmüş */}
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
          {/* Başlık bar */}
          <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-gray-100">
            <DialogTitle className="text-lg">
              {editing ? "Politikayı Düzenle" : "Yeni Politika"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editing ? `"${editing.title}" politikasını düzenliyorsunuz.` : "Yeni bir politika belgesi oluşturun."}
            </DialogDescription>
          </DialogHeader>

          {/* İki kolon gövde */}
          <form onSubmit={formik.handleSubmit} className="flex flex-1 overflow-hidden">

            {/* ── Sol: Form ── */}
            <div className="w-1/2 flex flex-col overflow-y-auto border-r border-gray-100 px-6 py-5 gap-4">

              {/* Başlık + Tür */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pol-title">Başlık *</Label>
                  <Input
                    id="pol-title"
                    name="title"
                    placeholder="Politika başlığı"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <p className="text-xs text-red-500">{formik.errors.title}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>Politika Türü *</Label>
                  <Select
                    value={String(formik.values.policyType)}
                    onValueChange={(v) => formik.setFieldValue("policyType", Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seçin…" />
                    </SelectTrigger>
                    <SelectContent>
                      {POLICY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={String(t.value)}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formik.touched.policyType && formik.errors.policyType && (
                    <p className="text-xs text-red-500">{formik.errors.policyType}</p>
                  )}
                </div>
              </div>

              {/* İçerik */}
              <div className="flex flex-col gap-1.5 flex-1">
                <Label>
                  İçerik * <span className="text-gray-400 font-normal text-xs">(HTML)</span>
                </Label>
                <Textarea
                  name="content"
                  placeholder="<p>Politika içeriği…</p>"
                  className="font-mono text-xs resize-none flex-1"
                  style={{ minHeight: 220 }}
                  value={formik.values.content}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.content && formik.errors.content && (
                  <p className="text-xs text-red-500">{formik.errors.content}</p>
                )}
              </div>

              {/* Meta */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pol-meta">Meta Açıklama</Label>
                <Textarea
                  id="pol-meta"
                  name="metaDescription"
                  rows={2}
                  placeholder="SEO için kısa açıklama"
                  value={formik.values.metaDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              {/* Dil + Versiyon + Sıra */}
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pol-lang">Dil *</Label>
                  <Input
                    id="pol-lang"
                    name="language"
                    placeholder="tr"
                    value={formik.values.language}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.language && formik.errors.language && (
                    <p className="text-xs text-red-500">{formik.errors.language}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pol-version">Versiyon</Label>
                  <Input
                    id="pol-version"
                    name="version"
                    type="number"
                    min={0}
                    value={formik.values.version}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pol-order">Sıra</Label>
                  <Input
                    id="pol-order"
                    name="order"
                    type="number"
                    min={0}
                    value={formik.values.order}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>

              {/* Aktif + Butonlar */}
              <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <Switch
                    id="pol-active"
                    checked={formik.values.isActive}
                    onCheckedChange={(v) => formik.setFieldValue("isActive", v)}
                  />
                  <Label htmlFor="pol-active" className="cursor-pointer text-sm">Aktif</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleClose}>
                    İptal
                  </Button>
                  <Button type="submit" size="sm" disabled={savingId !== null || formik.isSubmitting}>
                    {savingId !== null
                      ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Kaydediliyor…</>
                      : editing ? "Güncelle" : "Oluştur"}
                  </Button>
                </div>
              </div>
            </div>

            {/* ── Sağ: Önizleme ── */}
            <div className="w-1/2 flex flex-col overflow-hidden bg-gray-50">
              <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Canlı Önizleme</span>
              </div>
              <div className="flex-1 overflow-hidden">
                {formik.values.content ? (
                  <iframe
                    title="Politika Önizleme"
                    srcDoc={`<!doctype html><html><head><style>body{font-family:Georgia,serif;padding:32px 40px;font-size:15px;line-height:1.75;color:#1a1a1a;max-width:720px;margin:0 auto}h1,h2,h3{color:#111;margin-top:1.5em}p{margin:0.75em 0}a{color:#4f46e5}ul,ol{padding-left:1.5em}li{margin:0.4em 0}</style></head><body>${formik.values.content}</body></html>`}
                    className="w-full h-full border-0"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-300 text-sm select-none">
                    İçerik girdikçe önizleme burada görünür
                  </div>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Politikayı sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.title}</strong> politikası kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={handleDelete}>
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
